import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Briefcase, Backpack } from 'lucide-react';

const TestPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-black text-white font-sans selection:bg-white/20">
      {/* Navbar */}
      <nav className="flex items-center justify-between px-6 py-6 md:px-12">
        <div className="flex items-center">
          {/* Logo */}
          <div className="text-2xl font-normal italic tracking-tight" style={{ fontFamily: 'serif' }}>
            Defera
          </div>
        </div>
        
        <div className="flex items-center gap-8">
          <button 
            onClick={() => navigate('/')} 
            className="text-sm text-white/60 hover:text-white transition-colors"
          >
            Home
          </button>
          <button 
            onClick={() => navigate('/home?auth_mode=signup')}
            className="text-sm bg-[#1A1A1A] hover:bg-[#2A2A2A] text-white px-5 py-2.5 rounded-lg border border-white/10 transition-all"
          >
            Sign Up
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex flex-col items-center justify-center px-4 mt-12 md:mt-20 max-w-5xl mx-auto w-full">
        
        {/* Pill Badge */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <div className="border border-white/20 rounded-full px-4 py-1.5 text-xs text-white/80 bg-white/5 backdrop-blur-sm">
            Layaway service for flights
          </div>
        </motion.div>

        {/* Hero Text */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="text-center mb-12 space-y-4"
        >
          <h1 className="text-4xl md:text-6xl font-medium tracking-tight text-white/90">
            Book travel in
            <br />
            flexible installments
          </h1>
          <p className="text-lg md:text-xl text-white/50 font-light">
            Any flight, any hotel.
            <br className="md:hidden" /> No credit check. No interest.
          </p>
        </motion.div>

        {/* Search Button */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mb-16"
        >
          <button 
            onClick={() => navigate('/search')}
            className="bg-[#4ADE80] hover:bg-[#4ADE80]/90 text-black font-medium text-sm px-8 py-3 rounded-full transition-colors"
          >
            Search flights
          </button>
        </motion.div>

        {/* Flight Cards Container */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="w-full max-w-2xl space-y-4"
        >
          {/* Delta Flight Card */}
          <div className="bg-[#0A0A0A] border border-white/10 rounded-3xl p-6 md:p-8 hover:border-white/20 transition-all group">
            <div className="flex justify-between items-start mb-8">
              <div className="flex items-center gap-6">
                <div>
                  <div className="text-3xl font-normal mb-1">11:59 AM</div>
                  <div className="text-xl text-white/40 font-light">LAX</div>
                </div>
                <ArrowRight className="text-white/20 w-6 h-6" />
                <div>
                  <div className="text-3xl font-normal mb-1">1:16 PM</div>
                  <div className="text-xl text-white/40 font-light">LAS</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-normal mb-1">$15 today</div>
                <div className="text-lg text-white/40 font-light">$117 total</div>
              </div>
            </div>

            <div className="flex items-end justify-between">
              <div className="flex items-center gap-4">
                {/* Delta Logo (Simple Triangle Representation) */}
                <div className="w-10 h-10 flex items-center justify-center">
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8 text-[#E3132C]">
                    <path d="M12 2L2 22h20L12 2z" />
                  </svg>
                </div>
                <div>
                  <div className="text-lg font-light text-white/90">Nonstop · 1 hr 17 min</div>
                  <div className="text-base text-white/40 font-normal">Delta</div>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                 <div className="flex items-center gap-1.5 text-white/40">
                    <Briefcase className="w-5 h-5" strokeWidth={1.5} />
                    <span className="text-lg font-light">1</span>
                 </div>
                 <div className="flex items-center gap-1.5 text-white/40">
                    <Backpack className="w-5 h-5" strokeWidth={1.5} />
                    <span className="text-lg font-light">0</span>
                 </div>
                 <button className="ml-2 text-white/40 hover:text-white transition-colors">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="m6 9 6 6 6-6"/>
                    </svg>
                 </button>
              </div>
            </div>
          </div>

          {/* Southwest Flight Card */}
          <div className="bg-[#0A0A0A] border border-white/10 rounded-3xl p-6 md:p-8 hover:border-white/20 transition-all group">
            <div className="flex justify-between items-start mb-8">
              <div className="flex items-center gap-6">
                <div>
                  <div className="text-3xl font-normal mb-1">8:45 PM</div>
                  <div className="text-xl text-white/40 font-light">LAX</div>
                </div>
                <ArrowRight className="text-white/20 w-6 h-6" />
                <div>
                  <div className="text-3xl font-normal mb-1">9:55 PM</div>
                  <div className="text-xl text-white/40 font-light">LAS</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-normal mb-1">$15 today</div>
                <div className="text-lg text-white/40 font-light">$117 total</div>
              </div>
            </div>

            <div className="flex items-end justify-between">
              <div className="flex items-center gap-4">
                {/* Southwest Logo (Heart Representation) */}
                <div className="w-10 h-10 flex items-center justify-center">
                  <svg viewBox="0 0 24 24" fill="none" className="w-9 h-9">
                    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" fill="#0A1233" />
                    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" stroke="#4E6199" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M16.5 5.5c-1.3 0-2.5.7-3.2 1.8-.2.3-.6.3-.8 0-.7-1.1-1.9-1.8-3.2-1.8-2.2 0-4 1.8-4 4 0 2.5 2.5 5.2 6.7 9.1 4.2-3.9 6.7-6.6 6.7-9.1 0-2.2-1.8-4-4-4z" fill="#E41F35" />
                  </svg>
                </div>
                <div>
                  <div className="text-lg font-light text-white/90">Nonstop · 1 hr 10 min</div>
                  <div className="text-base text-white/40 font-normal">Southwest</div>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                 <div className="flex items-center gap-1.5 text-white/40">
                    <Briefcase className="w-5 h-5" strokeWidth={1.5} />
                    <span className="text-lg font-light">1</span>
                 </div>
                 <div className="flex items-center gap-1.5 text-white/40">
                    <Backpack className="w-5 h-5" strokeWidth={1.5} />
                    <span className="text-lg font-light">0</span>
                 </div>
                 <button className="ml-2 text-white/40 hover:text-white transition-colors">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="m6 9 6 6 6-6"/>
                    </svg>
                 </button>
              </div>
            </div>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default TestPage;
